// core
import React from "react";

// components
import LContainer from "../../LContainer/LContainer";
import Dropdown from "../../../../common/components/Forms/Dropdown/Dropdown";
import Toggle from "../../../../common/components/Forms/Toggle/Toggle";

// interfaces
import { IClient } from "../../../../common/interfaces/IClient";
import { IDropdownOption } from "../../../../common/interfaces/IDropdownOption";
import { Gender } from "../../../../common/interfaces/enums/Gender";
import { SmokerStatus } from "../../../../common/interfaces/enums/SmokerStatus";
import { useFormikContext } from "formik";
import { IFamilySettingsForm } from "../ClientDetailsSection/ClientDetailsSection";
import Button from "../../../../common/components/Forms/Button/Button";
import Icon from "../../../../common/components/Icon/Icon";
import cx from "classnames";
import useSettingsValidation from "../../../hooks/useSettingsValidation";

interface IClientBoxProps {
    index: number;
    client: IClient;
    title: string;
    onDelete: (client: IClient) => void;
    id?: string;
}

function range(start: number, end: number): Array<number> {
    const res = [];

    for (let i = start; i <= end; i++) {
        res.push(i);
    }

    return res;
}

const ClientBox: React.FC<IClientBoxProps> = (props) => {
    const { client, title, index, onDelete, id } = props;
    const { handleBlur, setFieldValue } = useFormikContext<IFamilySettingsForm>();
    const { getFormikError } = useSettingsValidation();

    const genderOptions = Object.keys(Gender)
        .filter((k) => k != Gender.Unknown)
        .map((k) => {
            return {
                selected: client.gender === Gender[k],
                id: Gender[k],
                label: Gender[k]
            };
        });

    const smokerOptions = Object.keys(SmokerStatus)
        .filter((k) => k != SmokerStatus.Unknown)
        .map((k) => {
            return {
                selected: client.smokerStatus === SmokerStatus[k],
                id: SmokerStatus[k],
                label: SmokerStatus[k]
            };
        });

    function getAgeOptions(start: number, end: number): IDropdownOption[] {
        return range(start, end)
            .map((i) => ({ value: i.toString(), label: i.toString() }))
            .map((i) => ({
                ...i,
                selected: client.age.toString() === i.value.toString()
            }));
    }

    function setProperty<K extends keyof IClient>(key: K, value: IClient[K]): void {
        setFieldValue(`clients.[${index}].${key}`, value);
    }

    const getError = (param: keyof IClient): string | boolean => {
        return getFormikError(`clients.[${index}].${param}`);
    };

    const ageOptions = client.isChild ? getAgeOptions(1, 15) : getAgeOptions(16, 75);

    return (
        <LContainer>
            <div id={id}>
                <h3
                    className={cx(
                        "title-build-client",
                        "layout-flex-row--centered",
                        "layout-flex-row--justify-spaced",
                        "flex-gap-m",
                        "gap-m"
                    )}>
                    {title}
                    {onDelete && (
                        <Button
                            component="button"
                            variant="box"
                            onClick={() => onDelete(client)}>
                            <Icon id="TrashCan" />
                        </Button>
                    )}
                </h3>

                <Toggle
                    label={`Gender`}
                    name={`clients.[${index}].gender`}
                    options={genderOptions}
                    onChange={(value) => {
                        setProperty("gender", value as Gender);
                    }}
                    hasError={getError("gender")}
                    className="gap-ml"
                />

                <Dropdown
                    id={`clients.[${index}].age`}
                    name={`clients.[${index}].age`}
                    title={`Age`}
                    selectedOptionId={client.age.toString()}
                    options={ageOptions}
                    onChange={(i) => {
                        setProperty("age", parseInt(i.target.value, 10));
                    }}
                    onBlur={handleBlur}
                    hasError={getError("age")}
                />

                {!client.isChild && (
                    <Toggle
                        label={
                            <React.Fragment>
                                Smoking status{" "}
                                <span className="text-no-bold">(past 12 months)</span>
                            </React.Fragment>
                        }
                        name={`clients.[${index}].smokerStatus`}
                        options={smokerOptions}
                        onChange={(value) => {
                            setProperty("smokerStatus", value as SmokerStatus);
                        }}
                        hasError={getError("smokerStatus")}
                        className="gap-ml"
                    />
                )}
            </div>
        </LContainer>
    );
};

export default ClientBox;
